import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Camera, CircleDollarSign, Clock3, LogIn, LogOut, Scissors, UserRound } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { DashboardSummary } from '../../../../../packages/contracts/src/index.js';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Select } from '../../components/ui/field';
import { ListDateFilters } from '../../components/ui/list-date-filters';
import { PaginationControls, useControlledPagination } from '../../components/ui/pagination';
import { SearchBox } from '../../components/ui/search';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/state';
import { Table, Td, Th } from '../../components/ui/table';
import { api } from '../../shared/api';
import { formatDateTime } from '../../shared/format';
import { dateRangeForPreset, isWithinDateRange, useUrlListFilters } from '../../shared/listFilters';
import { matchesSearch } from '../../shared/search';

type Activity = DashboardSummary['recentActivity'][number];

const activityTypeLabels: Record<Activity['type'], string> = {
  payment: 'Pago',
  grooming: 'Grooming',
  'check-in': 'Entrada',
  'check-out': 'Salida',
  photo: 'Foto',
  other: 'Otro',
};

export function DashboardActivityPage() {
  const defaultRange = useMemo(() => dateRangeForPreset('today'), []);
  const defaults = useMemo(() => ({ q: '', range: 'today', from: defaultRange.from, to: defaultRange.to, type: '', page: '1', pageSize: '10' }), [defaultRange]);
  const { values: filters, setValue, setValues } = useUrlListFilters(defaults, { keepDefaults: true });
  const page = Math.max(1, Number(filters.page) || 1);
  const pageSize = Math.max(10, Number(filters.pageSize) || 10);
  const summaryQuery = useQuery({
    queryKey: ['dashboard-summary', 'activity-list', filters.range, filters.from, filters.to],
    queryFn: () => api.dashboard({ period: filters.range, from: filters.from, to: filters.to }),
  });
  const activities = summaryQuery.data?.recentActivity ?? [];
  const filteredActivities = useMemo(() => activities.filter((activity) => {
    if (filters.type && activity.type !== filters.type) return false;
    return isWithinDateRange(activity.createdAt, filters.from, filters.to) && matchesSearch(filters.q, [activity.message, activity.actorName, activity.type, activityTypeLabels[activity.type], activity.createdAt]);
  }), [activities, filters.from, filters.q, filters.to, filters.type]);
  const activityPage = useControlledPagination(filteredActivities, page, (nextPage) => setValue('page', String(nextPage)), pageSize);

  if (summaryQuery.isLoading) return <LoadingState label="Cargando actividad" />;
  if (summaryQuery.isError) return <ErrorState message={summaryQuery.error.message} />;

  return (
    <div className="space-y-5">
      <div className="flex flex-col items-start gap-3">
        <Link className="inline-flex h-9 items-center gap-2 rounded-md border border-[#d6ded0] bg-white px-3 text-sm font-medium text-[#334033] hover:bg-[#eef3ea]" to="/admin/dashboard">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Dashboard
        </Link>
        <div>
          <h1 className="text-2xl font-semibold text-slate-950">Actividad reciente</h1>
          <p className="mt-1 text-sm text-slate-500">Busca por concepto, usuario, tipo o fecha.</p>
        </div>
      </div>
      <Card>
        <CardHeader>
          <div className="grid gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <SearchBox value={filters.q} onChange={(value) => setValue('q', value)} placeholder="Concepto, usuario o tipo" label="Buscar actividad" />
              <Select value={filters.type} onChange={(event) => setValue('type', event.target.value)} aria-label="Filtrar por tipo">
                <option value="">Todos los tipos</option>
                {Object.entries(activityTypeLabels).map(([type, label]) => <option key={type} value={type}>{label}</option>)}
              </Select>
            </div>
            <ListDateFilters range={filters.range} from={filters.from} to={filters.to} onChange={setValues} startId="activityFrom" endId="activityTo" />
          </div>
        </CardHeader>
        <CardContent>
          {activities.length === 0 ? <EmptyState title="Sin actividad" body="No hay actividad registrada en este rango." /> : null}
          {activities.length > 0 && filteredActivities.length === 0 ? <EmptyState title="Sin resultados" body="Intenta con otro filtro, búsqueda o rango de fechas." /> : null}
          {filteredActivities.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <thead><tr><Th>Fecha</Th><Th>Tipo</Th><Th>Actividad</Th><Th>Usuario</Th></tr></thead>
                <tbody>
                  {activityPage.pageItems.map((activity) => (
                    <tr key={activity.id}>
                      <Td>{formatDateTime(activity.createdAt)}</Td>
                      <Td><Badge>{activityTypeLabels[activity.type]}</Badge></Td>
                      <Td className="font-medium text-[#223126]">{activity.message}</Td>
                      <Td>{activity.actorName ? <span className="inline-flex items-center gap-1"><UserRound className="h-3.5 w-3.5" aria-hidden="true" />{activity.actorName}</span> : 'Sin usuario'}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : null}
          <PaginationControls {...activityPage} onPageChange={activityPage.setPage} onPageSizeChange={(nextPageSize) => setValues({ pageSize: String(nextPageSize), page: '1' })} />
        </CardContent>
      </Card>
    </div>
  );
}

export function DashboardActivityIcon({ type }: { readonly type: Activity['type'] }) {
  const icons = { payment: CircleDollarSign, grooming: Scissors, 'check-in': LogIn, 'check-out': LogOut, photo: Camera, other: Clock3 };
  const Icon = icons[type];
  return <Icon className="h-4 w-4" aria-hidden="true" />;
}
